import { Controller } from "@hotwired/stimulus"
import Cleave from "cleave.js"

export default class extends Controller {
  static targets = ["submitBtn", "firstName", "lastName", "phone", "role", "businessType", "errorBanner", "errorMessage"]
  static values = {
    firstNameRequired: String,
    lastNameRequired: String,
    roleRequired: String,
    phoneRequired: String
  }

  declare readonly firstNameRequiredValue: string
  declare readonly lastNameRequiredValue: string
  declare readonly roleRequiredValue: string
  declare readonly phoneRequiredValue: string
  declare readonly firstNameTarget: HTMLInputElement
  declare readonly lastNameTarget: HTMLInputElement
  declare readonly phoneTarget: HTMLInputElement
  declare readonly roleTarget: HTMLInputElement
  declare readonly roleTargets: HTMLInputElement[]
  declare readonly businessTypeTarget: HTMLInputElement
  declare readonly businessTypeTargets: HTMLInputElement[]
  declare readonly errorBannerTarget: HTMLElement
  declare readonly errorMessageTarget: HTMLElement

  declare readonly hasSubmitBtnTarget: boolean
  declare readonly hasFirstNameTarget: boolean
  declare readonly hasLastNameTarget: boolean
  declare readonly hasPhoneTarget: boolean
  declare readonly hasRoleTarget: boolean
  declare readonly hasBusinessTypeTarget: boolean
  declare readonly hasErrorBannerTarget: boolean
  declare readonly hasErrorMessageTarget: boolean

  connect() {
    this.toggleSubmitButton()
    
    // Clear errors on input and re-validate
    const inputs = this.element.querySelectorAll('input')
    inputs.forEach(input => {
      const handler = (event: Event) => {
        const fieldName = (event.target as HTMLInputElement).name
        this.clearFieldError(fieldName)
        this.toggleSubmitButton()
      }
      input.addEventListener('input', handler)
      input.addEventListener('change', handler)
    })

    const selects = this.element.querySelectorAll('select')
    selects.forEach(select => {
      select.addEventListener('change', (event) => {
        const fieldName = (event.target as HTMLSelectElement).name
        this.clearFieldError(fieldName)
        this.toggleSubmitButton()
      })
    })
  }

  toggleSubmitButton() {
    if (!this.hasSubmitBtnTarget) return
    
    const firstName = this.hasFirstNameTarget ? this.firstNameTarget.value.trim() : ''
    const lastName = this.hasLastNameTarget ? this.lastNameTarget.value.trim() : ''
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : ''
    
    let businessTypeSelected = true
    if (this.hasBusinessTypeTarget) {
      businessTypeSelected = this.businessTypeTargets.some(input => input.checked)
    }
    
    const isValid = firstName && lastName && phone && businessTypeSelected
    this.submitBtnTarget.disabled = !isValid
  }

  roleChanged() {
    if (!this.hasSubmitBtnTarget) return

    const roleInputs = this.hasRoleTarget ? this.roleTargets : Array.from(this.element.querySelectorAll('input[name="roles[]"], select[name="role"]'))
    
    if (roleInputs.length > 0) {
      const anyChecked = roleInputs.some(input => {
        const el = input as any
        if (el.type === 'checkbox' || el.type === 'radio') {
          return el.checked
        }
        return el.value && el.value !== ""
      })
      this.submitBtnTarget.disabled = !anyChecked
    }
  }

  submit(event: Event) {
    if (!this.validate()) {
      event.preventDefault()
      this.showGeneralError()
      return false
    }
  }

  validate() {
    this.clearErrors()

    let hasErrors = false

    if (this.hasFirstNameTarget) {
      const firstName = this.firstNameTarget.value.trim()
      if (!firstName) {
        this.showError('first_name', this.firstNameRequiredValue)
        hasErrors = true
      }
    }

    if (this.hasLastNameTarget) {
      const lastName = this.lastNameTarget.value.trim()
      if (!lastName) {
        this.showError('last_name', this.lastNameRequiredValue)
        hasErrors = true
      } else if (!/^[A-ZÁÉÍÓÚÑ]/.test(lastName)) {
        this.showError('last_name', (this.element as HTMLElement).dataset.lastNameCapital)
        hasErrors = true
      }
    }

    if (this.hasRoleTarget) {
      const isRoleSelected = this.roleTargets.some(role => {
        if (role instanceof HTMLSelectElement) {
          return role.value !== ""
        }
        return (role as HTMLInputElement).checked
      })

      if (!isRoleSelected) {
        this.showError('role', this.roleRequiredValue)
        hasErrors = true
      }
    }

    if (this.hasPhoneTarget) {
      const phone = this.phoneTarget.value.trim()
      if (!phone) {
        this.showError('phone', this.phoneRequiredValue)
        hasErrors = true
      }
    }

    return !hasErrors
  }

  showError(fieldName: string, message?: string) {
    const errorElement = this.element.querySelector(`[data-error="${fieldName}"]`)
    if (errorElement && message) {
      errorElement.textContent = message
      errorElement.classList.remove('hidden')
    }
  }

  showGeneralError() {
    if (this.hasErrorBannerTarget) {
      this.errorBannerTarget.classList.remove('hidden')
    }
  }

  hideGeneralError() {
    if (this.hasErrorBannerTarget) {
      this.errorBannerTarget.classList.add('hidden')
    }
  }

  clearErrors() {
    this.hideGeneralError()
    const errorElements = this.element.querySelectorAll('.error-message')
    errorElements.forEach(element => {
      element.textContent = ''
      element.classList.add('hidden')
    })
  }
  
  clearFieldError(fieldName: string) {
    const errorElement = this.element.querySelector(`[data-error="${fieldName}"]`)
    if (errorElement) {
      errorElement.textContent = ''
      errorElement.classList.add('hidden')
    }
    
    // Also hide general error if all field errors are gone
    const visibleErrors = this.element.querySelectorAll('.error-message:not(.hidden)')
    if (visibleErrors.length === 0) {
      this.hideGeneralError()
    }
  }
}
